/**
 * Voice AI — Agent Testing Tab
 * Browser-based test calls — talk to the AI agent directly from the dashboard.
 * Uses WebSocket audio streaming (no Twilio, no phone charges).
 *
 * Dispatches: toast
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { authService } from '../../services/auth-service.js';
import { BrowserCall } from '../../services/webrtc-service.js';

export class AgentTestingTab extends LitElement {
  static properties = {
    agent: { type: Object },
    _callState: { state: true },     // idle, connecting, active, ended
    _transcript: { state: true },     // Array of { speaker, text }
    _elapsed: { state: true },        // Seconds elapsed
    _error: { state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles, css`

    /* ─── Call Controls (glass card) ─── */
    .call-controls-inner {
      display: flex;
      align-items: center;
      gap: var(--space-4);
    }

    .call-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      border: none;
      cursor: pointer;
      font-size: 1.1rem;
      transition: all 0.2s;
      flex-shrink: 0;
    }

    .call-btn.start {
      background: var(--color-success);
      color: white;
    }
    .call-btn.start:hover { filter: brightness(1.1); }

    .call-btn.stop {
      background: var(--color-error);
      color: white;
      animation: pulse-ring 1.5s ease-out infinite;
    }
    .call-btn.stop:hover { filter: brightness(1.1); }

    .call-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      animation: none;
    }

    @keyframes pulse-ring {
      0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
      70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
      100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
    }

    .call-info {
      flex: 1;
      min-width: 0;
    }

    .call-status {
      font-family: var(--font-display);
      font-weight: var(--font-semibold);
      font-size: var(--text-base);
      color: var(--color-text-primary);
      display: flex;
      align-items: center;
      gap: var(--space-2);
    }

    .call-timer {
      font-family: var(--font-mono);
      font-size: var(--text-sm);
      color: var(--color-text-tertiary);
      font-variant-numeric: tabular-nums;
      margin-top: 2px;
    }

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .status-dot.connecting { background: var(--color-warning); animation: blink 1s infinite; }
    .status-dot.active { background: var(--color-success); }
    .status-dot.ended { background: var(--color-text-tertiary); }

    @keyframes blink {
      50% { opacity: 0.3; }
    }

    /* ─── Transcript (glass card body) ─── */
    .transcript-scroll {
      max-height: 400px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: var(--space-3);
      padding-top: var(--space-2);
    }

    .transcript-empty {
      text-align: center;
      color: var(--color-text-tertiary);
      font-family: var(--font-body);
      font-size: var(--text-sm);
      padding: var(--space-8) 0;
    }

    .transcript-bubble {
      padding: var(--space-2) var(--space-3);
      border-radius: var(--radius-md);
      font-family: var(--font-body);
      font-size: var(--text-sm);
      line-height: var(--leading-relaxed);
      max-width: 85%;
      word-wrap: break-word;
    }

    .transcript-bubble.customer {
      background: var(--color-accent);
      color: white;
      align-self: flex-end;
      border-bottom-right-radius: 4px;
    }

    .transcript-bubble.agent {
      background: var(--color-surface-secondary);
      color: var(--color-text-primary);
      border: 1px solid var(--color-border-primary);
      align-self: flex-start;
      border-bottom-left-radius: 4px;
    }

    .transcript-label {
      font-size: var(--text-xs);
      font-weight: var(--font-semibold);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 2px;
      opacity: 0.7;
    }

    /* ─── Error ─── */
    .error-msg {
      background: var(--color-error-bg);
      color: var(--color-error-text);
      padding: var(--space-3) var(--space-4);
      border-radius: var(--radius-md);
      font-family: var(--font-body);
      font-size: var(--text-sm);
      border: 1px solid rgba(220, 38, 38, 0.15);
      display: flex;
      align-items: center;
      gap: var(--space-2);
    }

    .mic-permission-note {
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
      display: flex;
      align-items: center;
      gap: var(--space-2);
    }
  `];

  constructor() {
    super();
    this._callState = 'idle';
    this._transcript = [];
    this._elapsed = 0;
    this._error = null;
    this._call = null;
    this._timer = null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._endCall();
  }

  render() {
    const agent = this.agent;
    if (!agent) return html``;

    return html`
      ${iconStyles}

      ${this._callState === 'idle' ? html`
        <div class="empty-state">
          <div class="empty-state-icon"><i class="fa-sharp fa-light fa-headset"></i></div>
          <div class="empty-state-title">Test ${agent.name}</div>
          <div class="empty-state-description">
            Start a test call to talk to this agent directly. No phone number or Twilio charges required.
          </div>
          <button class="btn btn-primary" style="margin-top:var(--space-4);" @click=${this._startCall}>
            <i class="fa-sharp fa-light fa-phone"></i>
            Start Test Call
          </button>
          <div class="mic-permission-note">
            <i class="fa-sharp fa-light fa-microphone"></i>
            Microphone access will be requested
          </div>
        </div>
      ` : html`
        <!-- Call Controls -->
        <div class="config-section">
          <div class="config-section-title">
            <i class="fa-sharp fa-light fa-phone-volume"></i>
            Test Call
          </div>
          <div class="config-section-desc">Testing ${agent.name} via browser audio</div>

          <div class="call-controls-inner" style="margin-top:var(--space-4);">
            ${this._callState === 'active' || this._callState === 'connecting' ? html`
              <button class="call-btn stop" @click=${this._endCall} title="End Call">
                <i class="fa-sharp fa-solid fa-phone-hangup"></i>
              </button>
            ` : html`
              <button class="call-btn start" @click=${this._startCall} title="Start New Call">
                <i class="fa-sharp fa-solid fa-phone"></i>
              </button>
            `}

            <div class="call-info">
              <div class="call-status">
                <span class="status-dot ${this._callState}"></span>
                ${this._callState === 'connecting' ? 'Connecting...'
                  : this._callState === 'active' ? 'In Call'
                  : 'Call Ended'}
              </div>
              ${this._elapsed > 0 ? html`
                <div class="call-timer">${this._formatTime(this._elapsed)}</div>
              ` : ''}
            </div>
          </div>

          ${this._error ? html`
            <div class="error-msg" style="margin-top:var(--space-3);">
              <i class="fa-sharp fa-light fa-circle-exclamation"></i> ${this._error}
            </div>
          ` : ''}
        </div>

        <!-- Live Transcript -->
        <div class="config-section">
          <div class="config-section-title">
            <i class="fa-sharp fa-light fa-message-lines"></i>
            Live Transcript
          </div>

          <div class="transcript-scroll" id="transcript-panel">
            ${this._transcript.length === 0 ? html`
              <div class="transcript-empty">
                ${this._callState === 'connecting' ? 'Connecting to agent...'
                  : this._callState === 'active' ? 'Waiting for speech...'
                  : 'No transcript'}
              </div>
            ` : this._transcript.map(t => html`
              <div class="transcript-bubble ${t.speaker}">
                <div class="transcript-label">${t.speaker === 'customer' ? 'You' : agent.name}</div>
                ${t.text}
              </div>
            `)}
          </div>
        </div>
      `}
    `;
  }

  async _startCall() {
    this._error = null;
    this._transcript = [];
    this._elapsed = 0;
    this._callState = 'connecting';

    try {
      // Get call token from PHP API
      const tokenRes = await authService.apiFetch('/voice/php/api/browser-call-token.php');
      const tokenData = await tokenRes.json();

      if (!tokenData?.success || !tokenData?.token) {
        throw new Error(tokenData?.error || 'Failed to get call token');
      }

      // Create browser call
      this._call = new BrowserCall({
        agentId: this.agent.id,
        token: tokenData.token,
        ujid: tokenData.ujid,
        wsUrl: tokenData.ws_url
      });

      // Event handlers
      this._call.addEventListener('state', (e) => {
        this._callState = e.detail.state;
        if (e.detail.state === 'active') {
          this._startTimer();
        }
      });

      this._call.addEventListener('transcript', (e) => {
        this._transcript = [...this._transcript, {
          speaker: e.detail.speaker,
          text: e.detail.text
        }];
        // Auto-scroll transcript panel
        this.updateComplete.then(() => {
          const panel = this.shadowRoot?.getElementById('transcript-panel');
          if (panel) panel.scrollTop = panel.scrollHeight;
        });
      });

      this._call.addEventListener('error', (e) => {
        this._error = e.detail || 'Connection error';
      });

      this._call.addEventListener('hangup', (e) => {
        this._stopTimer();
        this._callState = 'ended';
      });

      this._call.addEventListener('ended', () => {
        this._stopTimer();
        this._callState = 'ended';
      });

      await this._call.start();

    } catch (error) {
      this._error = error.message;
      this._callState = 'idle';
      this.dispatchEvent(new CustomEvent('toast', {
        bubbles: true, composed: true,
        detail: { message: `Call failed: ${error.message}`, type: 'error' }
      }));
    }
  }

  _endCall() {
    this._stopTimer();
    if (this._call) {
      this._call.stop();
      this._call = null;
    }
    if (this._callState === 'active' || this._callState === 'connecting') {
      this._callState = 'ended';
    }
  }

  _startTimer() {
    this._elapsed = 0;
    this._timer = setInterval(() => {
      this._elapsed++;
    }, 1000);
  }

  _stopTimer() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  _formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
}

customElements.define('agent-testing-tab', AgentTestingTab);
